import { AnalogInput, BoilerAdapter, get_z3k_object_state, GuardZone, HeatingCircuit, RadioSensor, RadioSensor433, } from 'z3k-config';
import { otHasError } from 'console/shared/components/banners/failure/components/fail-card/utils';
import { isLegacyDevice } from 'console/shared/contexts/device/config/utils';
import { selectLegacyBoilerAdapters } from 'console/shared/components/banners/failure/selectors';
import { any2unixtime, currentServerUnixTime } from 'webui/utils/time';
import { createSelector } from '@reduxjs/toolkit';
import { RfTypes } from 'z3k-parser/lib';
const select_legacy_sensor_alarm = (device) => {
    const { rf_status } = device;
    const current_server_time = currentServerUnixTime({
        local: parseInt((new Date().getTime() / 1e3).toString()),
        server: window?.PRELOAD?.server_time + 1,
    });
    const radio_sensors_alarm = Object.values(rf_status || {}).some((sensor) => {
        const { type, water, motion_until } = sensor;
        if (type === RfTypes.MLW) {
            return water === true;
        }
        if (type === RfTypes.MLM) {
            return motion_until > any2unixtime(current_server_time);
        }
        return false;
    });
    return radio_sensors_alarm;
};
const select_z3k_sensor_alarm = (z3k_config, z3k_state) => {
    const analog_inputs_alarm = AnalogInput.get.some(z3k_config, (sensor) => {
        if (sensor.sensor_type == AnalogInput.Type.THERMOSTAT) {
            return false;
        }
        const state = get_z3k_object_state(z3k_state, sensor.id);
        const alarm = state ? AnalogInput.isTriggered(state) : false;
        const is_worker = AnalogInput.isWorkingSensor(sensor);
        if (is_worker) {
            return false;
        }
        else {
            return alarm;
        }
    });
    const radio_sensors_alarm = RadioSensor.get.some(z3k_config, (sensor) => {
        const state = get_z3k_object_state(z3k_state, sensor.id);
        const alarm = state ? RadioSensor.isActive(state) : false;
        return alarm;
    });
    const radio_sensors_433_alarm = RadioSensor433.get.some(z3k_config, (sensor) => {
        const state = get_z3k_object_state(z3k_state, sensor.id);
        const alarm = state ? RadioSensor433.isTriggered(state) : false;
        const is_worker = RadioSensor433.isWorkingSensor(sensor);
        if (is_worker) {
            return false;
        }
        else {
            return alarm;
        }
    });
    return analog_inputs_alarm || radio_sensors_alarm || radio_sensors_433_alarm;
};
export function getGuardZoneAlarm(device) {
    const { z3k_config, z3k_state } = device;
    return GuardZone.get.some(z3k_config, (zone) => {
        const zone_state = get_z3k_object_state(z3k_state, zone.id);
        if (zone_state) {
            return GuardZone.isAlarm(zone_state);
        }
        return false;
    });
}
export function getSensorAlarm(device) {
    const { device_type, z3k_config, z3k_state } = device;
    if (isLegacyDevice(device_type)) {
        return select_legacy_sensor_alarm(device);
    }
    if (z3k_config) {
        return select_z3k_sensor_alarm(z3k_config, z3k_state);
    }
    return false;
}
function getLegacyBoilerStateIndicatorInfo(device) {
    const legacy_boilers = selectLegacyBoilerAdapters(device);
    return legacy_boilers.some((boiler) => boiler.is_fail);
}
const getHeatingCircuitsStateIndicatorInfo = (z3k_config, z3k_state) => {
    const boiler_adapters = BoilerAdapter.get.where(z3k_config, ({ id }) => HeatingCircuit.get.some(z3k_config, ({ control_devices }) => control_devices.includes(id)));
    const is_some_boiler_alarm = boiler_adapters.some((boiler) => {
        const { id } = boiler;
        const heating_circuit = HeatingCircuit.get.one(z3k_config, ({ control_devices }) => control_devices.includes(id));
        const { control_devices } = heating_circuit
            ? heating_circuit
            : { control_devices: [] };
        const boiler_adapter = BoilerAdapter.get.one(z3k_config, ({ id }) => {
            if (control_devices) {
                return control_devices.includes(id);
            }
            return false;
        });
        const state = heating_circuit && get_z3k_object_state(z3k_state, heating_circuit.id);
        const boiler_adapter_state = boiler_adapter && get_z3k_object_state(z3k_state, boiler_adapter.id);
        const is_alarm = (state && HeatingCircuit.isFail(state)) ||
            otHasError(boiler_adapter?.boiler_model, boiler_adapter_state?.ot, boiler_adapter_state?.status);
        return is_alarm;
    });
    const is_some_heating_circuit_alarm = HeatingCircuit.get.some(z3k_config, (heating_circuit) => {
        const { id } = heating_circuit;
        const heating_circuit_state = get_z3k_object_state(z3k_state, id);
        return heating_circuit_state
            ? HeatingCircuit.isFail(heating_circuit_state)
            : false;
    });
    return is_some_heating_circuit_alarm || is_some_boiler_alarm;
};
const selectHeatingCircuitAlarm = createSelector((device) => device?.z3k_config, (device) => device?.z3k_state, getHeatingCircuitsStateIndicatorInfo);
export function getDeviceHeatingCircuitsInfo(device) {
    if (isLegacyDevice(device.device_type)) {
        return getLegacyBoilerStateIndicatorInfo(device);
    }
    return selectHeatingCircuitAlarm(device);
}
